import React from 'react';

/** Roughly two stages per press. */
const SCROLL_STEP = 260;

export type RowOverflow = { left: boolean; right: boolean };

/**
 * Tracks which edges of a horizontally scrolling row have more content behind them.
 */
export const useRowOverflow = <T extends HTMLElement>(
  contents: string[]
): {
  rowRef: React.RefObject<T>;
  overflow: RowOverflow;
  measure: () => void;
  scrollBy: (direction: -1 | 1) => void;
} => {
  const rowRef = React.useRef<T>(null);
  const [overflow, setOverflow] = React.useState<RowOverflow>({
    left: false,
    right: false,
  });

  const contentKey = contents.join('\u0000');

  const measure = React.useCallback((): void => {
    const row = rowRef.current;
    if (!row) {
      return;
    }
    const maxScroll = row.scrollWidth - row.clientWidth;
    // 1px of slack for sub-pixel layout.
    const left = row.scrollLeft > 1;
    const right = maxScroll > 1 && row.scrollLeft < maxScroll - 1;
    setOverflow((previous) => (previous.left === left && previous.right === right ? previous : { left, right }));
  }, []);

  React.useEffect(() => {
    const row = rowRef.current;
    if (!row) {
      return undefined;
    }
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(row);
    Array.from(row.children).forEach((child) => resizeObserver.observe(child));
    return () => resizeObserver.disconnect();
  }, [measure, contentKey]);

  const scrollBy = React.useCallback((direction: -1 | 1): void => {
    rowRef.current?.scrollBy({
      left: direction * SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  return { rowRef, overflow, measure, scrollBy };
};
